"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import clsx from "clsx";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Columns3,
} from "lucide-react";

export type SortValue = number | string | boolean | null | undefined;

export interface Column<R> {
  id: string;
  label: string;
  /** Custom header content (defaults to `label`). */
  header?: ReactNode;
  /** Value used for sorting; columns without it are not sortable. */
  sortValue?: (row: R) => SortValue;
  cell: (row: R) => ReactNode;
  align?: "left" | "center" | "right";
  /** Hidden until enabled from the column picker. */
  defaultHidden?: boolean;
  /** Cannot be hidden (e.g. Ticker). */
  pinned?: boolean;
  title?: string;
}

export interface SortState {
  id: string;
  asc: boolean;
}

const PAGE_SIZE = 50;

/**
 * Nulls always sort last (stockweb stage / rs-growth behaviour); numbers
 * compare numerically, strings with localeCompare, booleans as 0/1.
 */
function compare(av: SortValue, bv: SortValue, asc: boolean): number {
  const an = av == null || (typeof av === "number" && Number.isNaN(av));
  const bn = bv == null || (typeof bv === "number" && Number.isNaN(bv));
  if (an && bn) return 0;
  if (an) return 1;
  if (bn) return -1;
  if (typeof av === "string" && typeof bv === "string") {
    return asc ? av.localeCompare(bv) : bv.localeCompare(av);
  }
  return asc ? Number(av) - Number(bv) : Number(bv) - Number(av);
}

export function DataTable<R>({
  rows,
  columns,
  rowKey,
  defaultSort,
  emptyText = "ไม่มีหุ้นที่ตรงเงื่อนไข",
  toolbar,
}: {
  rows: R[];
  columns: Column<R>[];
  rowKey: (row: R) => string;
  defaultSort: SortState;
  emptyText?: string;
  /** Rendered on the left of the table toolbar (counts, etc.). */
  toolbar?: ReactNode;
}) {
  const [sort, setSort] = useState<SortState>(defaultSort);
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id)),
  );
  const [page, setPage] = useState(1);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Back to page 1 whenever the filtered row set changes (stockweb resets the
  // stage page on any filter change). Adjusting state during render is the
  // React-recommended alternative to an effect here.
  const [prevRows, setPrevRows] = useState(rows);
  if (prevRows !== rows) {
    setPrevRows(rows);
    setPage(1);
  }

  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node))
        setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [pickerOpen]);

  const visible = columns.filter((c) => !hidden.has(c.id));
  const sortCol = columns.find((c) => c.id === sort.id);
  const sorted = useMemo(
    () =>
      sortCol?.sortValue
        ? [...rows].sort((a, b) =>
            compare(sortCol.sortValue!(a), sortCol.sortValue!(b), sort.asc),
          )
        : rows,
    [rows, sortCol, sort.asc],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageRows = sorted.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const from = sorted.length === 0 ? 0 : (current - 1) * PAGE_SIZE + 1;
  const to = Math.min(sorted.length, current * PAGE_SIZE);

  // stockweb toggleSort: same column flips direction, new column starts descending.
  const toggleSort = (id: string) =>
    setSort((s) => (s.id === id ? { id, asc: !s.asc } : { id, asc: false }));

  const toggleHidden = (id: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
          {toolbar}
        </div>
        <div ref={pickerRef} className="relative">
          <button
            type="button"
            onClick={() => setPickerOpen((o) => !o)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-2.5 py-1 text-xs text-muted hover:text-foreground"
          >
            <Columns3 size={13} /> คอลัมน์
          </button>
          {pickerOpen && (
            <div className="absolute right-0 top-full z-30 mt-2 max-h-80 w-56 overflow-y-auto rounded-xl border border-border bg-surface p-2 shadow-2xl">
              {columns
                .filter((c) => !c.pinned)
                .map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-xs hover:bg-white/[0.05]"
                  >
                    <input
                      type="checkbox"
                      checked={!hidden.has(c.id)}
                      onChange={() => toggleHidden(c.id)}
                      className="accent-[var(--accent)]"
                    />
                    {c.label}
                  </label>
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-h-[70vh] overflow-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-muted">
              {visible.map((c) => {
                const active = sort.id === c.id;
                return (
                  <th
                    key={c.id}
                    title={c.title}
                    aria-sort={
                      active
                        ? sort.asc
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                    className={clsx(
                      "sticky top-0 z-10 whitespace-nowrap border-b border-border bg-surface px-3 py-2.5 font-medium",
                      c.align === "right"
                        ? "text-right"
                        : c.align === "center"
                          ? "text-center"
                          : "text-left",
                    )}
                  >
                    {c.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.id)}
                        className={clsx(
                          "inline-flex items-center gap-1 uppercase tracking-wider hover:text-foreground",
                          active && "text-foreground",
                        )}
                      >
                        {c.header ?? c.label}
                        {active &&
                          (sort.asc ? (
                            <ArrowUp size={11} />
                          ) : (
                            <ArrowDown size={11} />
                          ))}
                      </button>
                    ) : (
                      (c.header ?? c.label)
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pageRows.map((row) => (
              <tr
                key={rowKey(row)}
                className="transition-colors hover:bg-white/[0.03]"
              >
                {visible.map((c) => (
                  <td
                    key={c.id}
                    className={clsx(
                      "whitespace-nowrap px-3 py-2",
                      c.align === "right"
                        ? "text-right"
                        : c.align === "center"
                          ? "text-center"
                          : "text-left",
                    )}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
            {pageRows.length === 0 && (
              <tr>
                <td
                  colSpan={visible.length}
                  className="py-12 text-center text-sm text-muted"
                >
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
        <span className="tabular-nums">
          แสดง <span className="text-foreground">{from}</span>–
          <span className="text-foreground">{to}</span> จาก{" "}
          <span className="text-foreground">{sorted.length}</span> หุ้น
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setPage(Math.max(1, current - 1))}
            disabled={current === 1}
            aria-label="หน้าก่อน"
            className="rounded-lg border border-border bg-surface-2 p-1 hover:text-foreground disabled:opacity-40"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="tabular-nums">
            หน้า {current} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, current + 1))}
            disabled={current === totalPages}
            aria-label="หน้าถัดไป"
            className="rounded-lg border border-border bg-surface-2 p-1 hover:text-foreground disabled:opacity-40"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
